"use client";
import { PasswordInputFormField } from "@/components/form/password-input-form-field";
import { Spinner } from "@/components/shadcn/spinner";
import TurnstileWidget, {
    TurnstileWidgetHandle,
} from "@/components/turnstile-widget";
import { Link, useRouter } from "@/i18n/navigation";
import {
    trackSignUpCompleted,
    trackSignUpFailed,
    trackSignUpStarted,
} from "@/lib/analytics";
import { authClient } from "@/lib/auth/client";
import { buildPasswordComplexitySchema } from "@/lib/auth/password-policy";
import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { ChevronRight } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "../shadcn/button";
import { Checkbox } from "../shadcn/checkbox";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../shadcn/form";
import { Input } from "../shadcn/input";
import { Label } from "../shadcn/label";

const USERNAME_REGEX = /^[a-z0-9çğıöşüə_.]+$/; // i18n-ignore: supported username characters

const authErrorMessageKeys = {
    INVALID_EMAIL: "invalidEmailDetailed",
    INVALID_PASSWORD: "passwordRule",
    INVALID_USERNAME: "usernamePattern",
    PASSWORD_TOO_LONG: "passwordTooLong",
    PASSWORD_TOO_SHORT: "passwordTooShort",
    USER_ALREADY_EXISTS: "userAlreadyExists",
    USERNAME_IS_ALREADY_TAKEN: "usernameTaken",
    USERNAME_IS_REQUIRED: "usernameRequired",
    USERNAME_TOO_SHORT: "usernameTooShort",
    USERNAME_TOO_LONG: "usernameTooLong",
} as const;

type AuthErrorCode = keyof typeof authErrorMessageKeys;

const isAuthErrorCode = (code?: string): code is AuthErrorCode => {
    return !!code && code in authErrorMessageKeys;
};

const RegisterForm = () => {
    const t = useTranslations("auth");
    const locale = useLocale();
    const [isLoading, setIsLoading] = useState(false);
    const [captchaToken, setCaptchaToken] = useState("");
    const captchaRef = useRef<TurnstileWidgetHandle>(null);
    const router = useRouter();

    const getAuthErrorMessage = (code?: string, fallback?: string) => {
        if (isAuthErrorCode(code)) {
            return t(authErrorMessageKeys[code]);
        }

        return fallback || t("accountCreationFailed");
    };

    const passwordRequirements = {
        minLength: t("passwordMin"),
        uppercase: t("passwordUppercase"),
        lowercase: t("passwordLowercase"),
        number: t("passwordNumber"),
    };
    const formSchema = z
        .object({
            username: z
                .string({
                    required_error: t("usernameRequired"),
                })
                .trim()
                .min(1, t("usernameRequired"))
                .transform((value) =>
                    value.normalize("NFC").toLocaleLowerCase("tr-TR"),
                )
                .pipe(
                    z
                        .string()
                        .min(3, t("usernameMin"))
                        .max(30, t("usernameMax"))
                        .regex(USERNAME_REGEX, t("usernamePattern")),
                ),
            email: z
                .string({ required_error: t("emailRequired") })
                .email(t("emailInvalid")),
            Password: z
                .string({ required_error: t("passwordRequired") })
                .min(1, t("passwordRequired"))
                .and(buildPasswordComplexitySchema(passwordRequirements)),
            confirmPassword: z
                .string({ required_error: t("passwordConfirmRequired") })
                .min(1, t("passwordConfirmRequired")),
            clarification: z
                .boolean()
                .refine((value) => value, t("consentRequired")),
        })
        .superRefine((values, ctx) => {
            if (values.Password !== values.confirmPassword) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t("passwordsMustMatch"),
                    path: ["confirmPassword"],
                });
            }
        });
    type InputType = z.infer<typeof formSchema>;

    const form = useForm<InputType>({
        resolver: zodResolver(formSchema),
        mode: "onSubmit",
        reValidateMode: "onChange",
        defaultValues: {
            username: "",
            email: "",
            Password: "",
            confirmPassword: "",
            clarification: false,
        },
    });

    async function onSubmit(values: InputType) {
        trackSignUpStarted(locale);
        if (!captchaToken) {
            trackSignUpFailed(locale, "turnstile_pending");
            toast.error(t("loginFailed"), {
                description: t("turnstilePending"),
            });
            return;
        }

        try {
            setIsLoading(true);

            const captchaResponse = await axios.post("/api/turnstile-verify", {
                token: captchaToken,
            });

            if (captchaResponse?.data?.success === false) {
                trackSignUpFailed(locale, "turnstile_failed");
                toast.error(t("loginFailed"), {
                    description: t("turnstileFailed"),
                });
                return;
            }

            const { error } = await authClient.signUp.email({
                email: values.email,
                password: values.Password,
                name: values.username,
                username: values.username,
            });

            if (error) {
                trackSignUpFailed(locale, error.code || "unknown");
                const description = getAuthErrorMessage(
                    error.code,
                    error.message,
                );

                if (
                    error.code === "INVALID_USERNAME" ||
                    error.code?.startsWith("USERNAME_")
                ) {
                    form.setError("username", { message: description });
                }

                if (
                    error.code === "INVALID_PASSWORD" ||
                    error.code?.startsWith("PASSWORD_")
                ) {
                    form.setError("Password", { message: description });
                }

                if (error.code === "INVALID_EMAIL") {
                    form.setError("email", { message: description });
                }

                toast.error(t("somethingWrong"), { description });
            } else {
                trackSignUpCompleted(locale);
                toast.success(t("registerSuccessTitle"), {
                    description: t("registerSuccessDesc"),
                });
                router.push("/check-mailbox-registration-sent");
            }
        } catch (error) {
            trackSignUpFailed(locale, "network_error");
            console.error(error);
            toast.error(t("somethingWrong"), {
                description: t("registerFailedDesc"),
            });
        } finally {
            captchaRef.current?.reset();
            setIsLoading(false);
        }
    }

    function onInvalid(errors: Record<string, unknown>) {
        trackSignUpStarted(locale);
        trackSignUpFailed(
            locale,
            `client_validation:${Object.keys(errors).sort().join(",")}`,
        );
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
                <div className="flex flex-col gap-5 sm:gap-6">
                    <FormField
                        control={form.control}
                        name="username"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t("usernameLabel")}</FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder={t("usernamePlaceholder")}
                                        className={`${
                                            form.formState.errors.username &&
                                            "border-danger"
                                        }`}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>E-posta Adresi</FormLabel>
                                <FormControl>
                                    <Input
                                        type="text"
                                        placeholder="E-posta adresinizi girin"
                                        className={`${
                                            form.formState.errors.email &&
                                            "border-danger"
                                        }`}
                                        {...field}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <PasswordInputFormField
                        control={form.control}
                        name="Password"
                        label={t("passwordLabel")}
                        placeholder={t("passwordPlaceholder")}
                        autoComplete="new-password"
                        passwordRequirements={passwordRequirements}
                    />
                    <PasswordInputFormField
                        control={form.control}
                        name="confirmPassword"
                        label={t("passwordConfirmLabel")}
                        placeholder={t("passwordConfirmPlaceholder")}
                        autoComplete="new-password"
                    />

                    <FormField
                        control={form.control}
                        name="clarification"
                        render={({ field }) => (
                            <FormItem>
                                <div className="flex items-start gap-2">
                                    <FormControl id="clarification">
                                        <Checkbox
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                    </FormControl>
                                    <Label
                                        className="text-sm leading-relaxed"
                                        htmlFor="clarification"
                                    >
                                        <Link
                                            href={
                                                "/sozlesmeler/aydinlatma-metni"
                                            }
                                            className="text-primary hover:text-primary-foreground"
                                        >
                                            {t("consentLink")}
                                        </Link>
                                        {t("consentSuffix")}
                                    </Label>
                                </div>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <TurnstileWidget
                        ref={captchaRef}
                        onToken={setCaptchaToken}
                        action="inquirySubmit"
                    />

                    <div className="flex items-center justify-between">
                        <Button
                            className="flex w-full justify-between pr-4 pl-10"
                            disabled={isLoading || !captchaToken}
                        >
                            <span>{t("start")}</span>{" "}
                            {isLoading ? (
                                <Spinner data-icon="inline-end" />
                            ) : (
                                <ChevronRight data-icon="inline-end" />
                            )}
                        </Button>
                    </div>
                </div>
            </form>
        </Form>
    );
};

export default RegisterForm;
